import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCompanyMutation } from "@/hooks/useCompanyMutation";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

export const serviceSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(3, "Nome muito curto"),
  description: z.string().optional(),
  price: z.number().min(0, "Preço inválido"),
  time_minutes: z.number().min(1, "Tempo inválido"),
});

export type ServiceFormValues = z.infer<typeof serviceSchema>;

export function useServicesData(
  activeCompanyId: string | null,
  closeModal: () => void,
  resetForm: () => void,
) {
  const queryClient = useQueryClient();

  const { data: services, isLoading } = useQuery({
    queryKey: ["services", activeCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select("*")
        .eq("company_id", activeCompanyId)
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!activeCompanyId,
  });

  const saveService = useCompanyMutation({
    mutationFn: async (values: ServiceFormValues & { company_id?: string }) => {
      if (values.id) {
        const { error } = await supabase
          .from("services")
          .update({
            name: values.name,
            description: values.description,
            price: values.price,
            time_minutes: values.time_minutes,
          })
          .eq("id", values.id)
          .eq("company_id", values.company_id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("services").insert({
          id: crypto.randomUUID(),
          company_id: values.company_id,
          name: values.name,
          description: values.description,
          price: values.price,
          time_minutes: values.time_minutes,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["services", activeCompanyId],
      });
      closeModal();
      resetForm();
    },
    onError: (err: any) => {
      console.error(err);
      alert(`Erro ao salvar: ${err.message}\nCompany: ${activeCompanyId}`);
    },
  });

  const deleteService = useCompanyMutation({
    mutationFn: async ({
      id,
      company_id,
    }: {
      id: string;
      company_id?: string;
    }) => {
      const { error } = await supabase
        .from("services")
        .delete()
        .eq("id", id)
        .eq("company_id", company_id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["services", activeCompanyId],
      });
    },
  });

  return {
    services,
    isLoading,
    saveService,
    deleteService,
  };
}
